import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Globe2, Handshake, Loader2, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { COUNTRY_PRESETS } from '../lib/localization';
import { supabase } from '../lib/supabase';
import { maskCep, maskCpfCnpj, maskPhone } from '../utils/masks';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve) => {
      if (window.turnstile) resolve();
      else existing.addEventListener('load', () => resolve());
    });
  }
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

const channelOptions = [
  { value: 'indicacao_direta', pt: 'Indicação direta', es: 'Indicación directa', en: 'Direct referral' },
  { value: 'visita_presencial', pt: 'Visita presencial', es: 'Visita presencial', en: 'In-person visit' },
  { value: 'whatsapp', pt: 'WhatsApp', es: 'WhatsApp', en: 'WhatsApp' },
  { value: 'instagram', pt: 'Instagram', es: 'Instagram', en: 'Instagram' },
  { value: 'base_clientes', pt: 'Base de clientes', es: 'Base de clientes', en: 'Customer base' },
  { value: 'consultoria_oficinas', pt: 'Consultoria para oficinas', es: 'Consultoría para talleres', en: 'Shop consulting' },
];

const countryLabels = {
  BR: 'Brasil',
  ES: 'España',
  PT: 'Portugal',
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  MX: 'México',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
};

const emptyForm = {
  nome: '',
  documento_tipo: 'cpf',
  documento_numero: '',
  data_nascimento: '',
  email: '',
  telefone: '',
  pais_codigo: 'BR',
  cidade: '',
  estado: '',
  endereco: '',
  bairro: '',
  cep: '',
  redes_sociais: '',
  experiencia: '',
  vende_software: '',
  carteira_oficinas: '',
  mensagem: '',
  canais_venda: [],
  aceita_termos: false,
  website: '',
};

const copyByLanguage = {
  pt: {
    title: 'Parceiro Comercial MecSync',
    back: 'Voltar',
    badge: 'Parceiro comercial GDN',
    headline: 'Venda MecSync e receba comissão recorrente.',
    intro: 'A GDN aprova os parceiros, define percentual, prazo e teto de comissão, e acompanha cada venda pelo painel financeiro. Você indica, vende e participa do crescimento das oficinas.',
    highlights: [
      ['Comissão controlada por contrato', 'Percentual, valor fixo, quantidade de pagamentos e teto definidos pelos admins GDN.'],
      ['Venda com foco em oficinas reais', 'MecSync tem OS, agenda, financeiro, check-in, orçamento digital, IA e automação comercial.'],
      ['Aprovação antes de operar', 'Todo candidato passa por análise para preservar qualidade comercial e margem.'],
    ],
    formTitle: 'Quero ser parceiro',
    formIntro: 'Preencha seus dados. A equipe GDN entra em contato para alinhar regras de comissão e próximos passos.',
    successTitle: 'Candidatura recebida',
    successText: 'Agora a equipe GDN avalia seu perfil e define percentual, prazo e regras de comissão antes de liberar sua operação comercial.',
    successBadge: 'Solicitação registrada',
    protocolLabel: 'Protocolo da candidatura',
    successNote: 'O envio da candidatura não representa aprovação automática ou vínculo comercial.',
    sendAnother: 'Enviar outro cadastro',
    backSite: 'Voltar para o site',
    requiredMain: 'Informe nome, documento, email e telefone.',
    requiredAddress: 'Informe cidade, região e endereço.',
    requiredExperience: 'Informe sua experiência e pelo menos um canal de venda.',
    requiredTerms: 'Aceite os termos de candidatura para continuar.',
    submitError: 'Não foi possível enviar sua candidatura.',
    submitSuccess: 'Cadastro recebido. Nossa equipe vai analisar seu perfil.',
    fields: {
      name: 'Nome completo *',
      document: 'Documento *',
      birth: 'Data de nascimento',
      email: 'Email *',
      phone: 'WhatsApp *',
      country: 'País de atuação',
      city: 'Cidade',
      region: 'UF/Região',
      experience: 'Experiência comercial ou segmento de atuação',
      address: 'Endereço completo *',
      district: 'Bairro',
      postal: 'CEP',
      socials: 'Instagram, LinkedIn, site ou rede profissional',
      sellsSoftware: 'Já vende software/marketing/automação?',
      yes: 'Sim',
      no: 'Não',
      portfolio: 'Quantas oficinas você consegue abordar?',
      channels: 'Canais de venda',
      messageTitle: 'Mensagem para a GDN',
      message: 'Conte como pretende vender, região de atuação, experiência com oficinas ou tecnologia...',
      terms: 'Confirmo que os dados informados são verdadeiros e aceito que a GDN analise meu perfil antes de liberar percentual, prazo, regras de comissão e uso comercial da marca MecSync.',
      sending: 'Enviando...',
      submit: 'Enviar candidatura',
    },
  },
  es: {
    title: 'Partner Comercial MecSync',
    back: 'Volver',
    badge: 'Partner comercial GDN',
    headline: 'Vende MecSync y recibe comisión recurrente.',
    intro: 'GDN aprueba a los partners, define porcentaje, plazo y límites de comisión, y acompaña cada venta en el panel financiero.',
    highlights: [
      ['Comisión controlada por contrato', 'Porcentaje, valor fijo, cantidad de pagos y límites definidos por los admins GDN.'],
      ['Venta enfocada en talleres reales', 'MecSync tiene órdenes, agenda, finanzas, check-in, presupuesto digital, IA y automatización comercial.'],
      ['Aprobación antes de operar', 'Todo candidato pasa por análisis para preservar calidad comercial y margen.'],
    ],
    formTitle: 'Quiero ser partner',
    formIntro: 'Completa tus datos. El equipo GDN te contactará para alinear reglas de comisión y próximos pasos.',
    successTitle: 'Candidatura recibida',
    successText: 'Ahora el equipo GDN evalúa tu perfil y define porcentaje, plazo y reglas de comisión antes de liberar tu operación comercial.',
    successBadge: 'Solicitud registrada',
    protocolLabel: 'Protocolo de la candidatura',
    successNote: 'El envío de la candidatura no representa aprobación automática ni vínculo comercial.',
    sendAnother: 'Enviar otro registro',
    backSite: 'Volver al sitio',
    requiredMain: 'Informa nombre, documento, email y teléfono.',
    requiredAddress: 'Informa ciudad, región y dirección.',
    requiredExperience: 'Informa tu experiencia y al menos un canal de venta.',
    requiredTerms: 'Acepta los términos de candidatura para continuar.',
    submitError: 'No fue posible enviar tu candidatura.',
    submitSuccess: 'Registro recibido. Nuestro equipo analizará tu perfil.',
    fields: {
      name: 'Nombre completo *',
      document: 'Documento *',
      birth: 'Fecha de nacimiento',
      email: 'Email *',
      phone: 'WhatsApp *',
      country: 'País de actuación',
      city: 'Ciudad',
      region: 'Provincia/Región',
      experience: 'Experiencia comercial o segmento de actuación',
      address: 'Dirección completa *',
      district: 'Barrio',
      postal: 'Código postal',
      socials: 'Instagram, LinkedIn, sitio o red profesional',
      sellsSoftware: '¿Ya vendes software/marketing/automatización?',
      yes: 'Sí',
      no: 'No',
      portfolio: '¿Cuántos talleres puedes abordar?',
      channels: 'Canales de venta',
      messageTitle: 'Mensaje para GDN',
      message: 'Cuenta cómo piensas vender, región de actuación, experiencia con talleres o tecnología...',
      terms: 'Confirmo que los datos informados son verdaderos y acepto que GDN analice mi perfil antes de liberar porcentaje, plazo, reglas de comisión y uso comercial de la marca MecSync.',
      sending: 'Enviando...',
      submit: 'Enviar candidatura',
    },
  },
  en: {
    title: 'MecSync Commercial Partner',
    back: 'Back',
    badge: 'GDN commercial partner',
    headline: 'Sell MecSync and earn recurring commission.',
    intro: 'GDN approves partners, defines percentage, duration, and commission caps, and tracks every sale through the finance panel.',
    highlights: [
      ['Commission controlled by contract', 'Percentage, fixed value, number of payments, and caps defined by GDN admins.'],
      ['Sales focused on real repair shops', 'MecSync includes orders, schedule, finance, check-in, digital quotes, AI, and commercial automation.'],
      ['Approval before selling', 'Every candidate is reviewed to preserve sales quality and margin.'],
    ],
    formTitle: 'I want to become a partner',
    formIntro: 'Fill in your details. The GDN team will contact you to align commission rules and next steps.',
    successTitle: 'Application received',
    successText: 'The GDN team will now review your profile and define percentage, duration, and commission rules before enabling your commercial operation.',
    successBadge: 'Application registered',
    protocolLabel: 'Application reference',
    successNote: 'Submitting an application does not represent automatic approval or a commercial relationship.',
    sendAnother: 'Send another application',
    backSite: 'Back to site',
    requiredMain: 'Enter name, document, email, and phone.',
    requiredAddress: 'Enter city, region, and address.',
    requiredExperience: 'Enter your experience and at least one sales channel.',
    requiredTerms: 'Accept the application terms to continue.',
    submitError: 'Could not send your application.',
    submitSuccess: 'Application received. Our team will review your profile.',
    fields: {
      name: 'Full name *',
      document: 'Document *',
      birth: 'Date of birth',
      email: 'Email *',
      phone: 'WhatsApp *',
      country: 'Operating country',
      city: 'City',
      region: 'State/Region',
      experience: 'Commercial experience or business segment',
      address: 'Full address *',
      district: 'District',
      postal: 'Postal code',
      socials: 'Instagram, LinkedIn, website, or professional network',
      sellsSoftware: 'Do you already sell software/marketing/automation?',
      yes: 'Yes',
      no: 'No',
      portfolio: 'How many shops can you approach?',
      channels: 'Sales channels',
      messageTitle: 'Message to GDN',
      message: 'Tell us how you plan to sell, your region, and your experience with repair shops or technology...',
      terms: 'I confirm the information is true and accept that GDN will review my profile before enabling percentage, duration, commission rules, and commercial use of the MecSync brand.',
      sending: 'Sending...',
      submit: 'Send application',
    },
  },
};

function countryCodeFromValue(value) {
  const countryCode = String(value || '').trim().toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  return COUNTRY_PRESETS[countryCode] ? countryCode : '';
}

function countryCodeFromBrowser() {
  const language = navigator.languages?.[0] || navigator.language || 'pt-BR';
  if (/^es-ES/i.test(language)) return 'ES';
  if (/^pt-PT/i.test(language)) return 'PT';
  if (/^es-MX/i.test(language)) return 'MX';
  if (/^es-AR/i.test(language)) return 'AR';
  if (/^es-CL/i.test(language)) return 'CL';
  if (/^es-CO/i.test(language)) return 'CO';
  if (/^en-GB/i.test(language)) return 'GB';
  if (/^en-CA/i.test(language)) return 'CA';
  if (/^en/i.test(language)) return 'US';
  if (/^es/i.test(language)) return 'ES';
  return 'BR';
}

function languageKeyFromCountry(countryCode) {
  const preset = COUNTRY_PRESETS[countryCode] || COUNTRY_PRESETS.BR;
  if (preset.language?.startsWith('es')) return 'es';
  if (preset.language?.startsWith('en')) return 'en';
  return 'pt';
}

function countryOptionLabel(code, preset) {
  return `${countryLabels[code] || code} - ${preset.currency}`;
}

export default function PartnerSignup() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const copy = useMemo(() => copyByLanguage[languageKeyFromCountry(form.pais_codigo)] || copyByLanguage.pt, [form.pais_codigo]);
  const selectedChannels = useMemo(() => new Set(form.canais_venda), [form.canais_venda]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileContainerRef.current) return undefined;
    let cancelled = false;

    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile || !turnstileContainerRef.current) return;
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
    });

    return () => {
      cancelled = true;
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  function resetCaptcha() {
    setCaptchaToken('');
    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const explicitCountry = countryCodeFromValue(params.get('country') || params.get('pais') || params.get('country_code') || '');
    const storedCountry = countryCodeFromValue(window.localStorage.getItem('mecsync_signup_country') || '');
    const countryCode = explicitCountry || storedCountry || countryCodeFromBrowser();
    setForm((prev) => ({ ...prev, pais_codigo: countryCode }));
  }, []);

  useEffect(() => {
    document.title = copy.title;
  }, [copy.title]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleChannel(channel) {
    setForm((prev) => ({
      ...prev,
      canais_venda: prev.canais_venda.includes(channel)
        ? prev.canais_venda.filter((item) => item !== channel)
        : [...prev.canais_venda, channel],
    }));
  }

  async function submitApplication(event) {
    event.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.telefone.trim() || !form.documento_numero.trim()) {
      toast.error(copy.requiredMain);
      return;
    }
    if (!form.cidade.trim() || !form.estado.trim() || !form.endereco.trim()) {
      toast.error(copy.requiredAddress);
      return;
    }
    if (!form.experiencia.trim() || form.canais_venda.length === 0) {
      toast.error(copy.requiredExperience);
      return;
    }
    if (!form.aceita_termos) {
      toast.error(copy.requiredTerms);
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error(copy.requiredCaptcha || 'Confirme a verificação de segurança antes de enviar.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-partner-application', {
        body: {
          nome: form.nome.trim(),
          documento_tipo: form.documento_tipo,
          documento_numero: form.documento_numero.trim(),
          data_nascimento: form.data_nascimento || null,
          email: form.email.trim().toLowerCase(),
          telefone: form.telefone.trim(),
          pais_codigo: form.pais_codigo,
          cidade: form.cidade.trim() || null,
          estado: form.estado.trim().toUpperCase().slice(0, 32) || null,
          endereco: form.endereco.trim() || null,
          bairro: form.bairro.trim() || null,
          cep: form.cep.trim() || null,
          redes_sociais: form.redes_sociais.trim() || null,
          experiencia: form.experiencia.trim() || null,
          vende_software: form.vende_software === 'sim' ? true : form.vende_software === 'nao' ? false : null,
          canais_venda: form.canais_venda,
          carteira_oficinas: form.carteira_oficinas ? Number(form.carteira_oficinas) : null,
          mensagem: form.mensagem.trim() || null,
          aceita_termos: true,
          website: form.website,
          captcha_token: captchaToken,
        },
      });

      if (error) {
        throw new Error(error.message || copy.submitError);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.created !== true || !data?.application_id) {
        throw new Error('O servidor não confirmou o recebimento da candidatura.');
      }

      setApplicationId(data.application_id);
      setSubmitted(true);
      setForm((prev) => ({ ...emptyForm, pais_codigo: prev.pais_codigo }));
      resetCaptcha();
      toast.success(copy.submitSuccess);
    } catch (error) {
      resetCaptcha();
      toast.error(error.message || copy.submitError);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    const protocol = applicationId
      ? `GDN-${applicationId.replace(/-/g, '').slice(0, 10).toUpperCase()}`
      : '';

    return (
      <main className="min-h-screen bg-[#080809] text-zinc-100">
        <section className="border-b border-white/10 px-4 py-6">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              className="font-display text-2xl font-black"
            >
              Mec<span className="text-orange-500">Sync</span>
            </button>

            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600">
              GDN Corp
            </span>
          </div>
        </section>

        <div className="mx-auto flex min-h-[75vh] w-full max-w-2xl items-center justify-center px-4 py-12">
          <section className="w-full rounded-2xl border border-white/[0.08] bg-[#111113] px-6 py-10 shadow-2xl shadow-black/30 sm:px-10 sm:py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-orange-400/25 bg-orange-500/10 text-orange-400">
              <CheckCircle2 size={24} strokeWidth={1.8} />
            </div>

            <div className="mt-7">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-400">
                {copy.successBadge}
              </span>

              <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
                {copy.successTitle}
              </h1>

              <p className="mt-4 max-w-xl text-sm leading-7 text-zinc-400 sm:text-base">
                {copy.successText}
              </p>
            </div>

            {protocol && (
              <div className="mt-8 border-y border-white/[0.07] py-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  {copy.protocolLabel}
                </div>

                <div className="mt-2 font-mono text-sm font-medium tracking-wide text-zinc-200">
                  {protocol}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                className="justify-center"
                onClick={() => { window.location.href = '/'; }}
              >
                <ArrowLeft size={16} />
                {copy.backSite}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="justify-center"
                onClick={() => {
                  setApplicationId('');
                  setSubmitted(false);
                }}
              >
                {copy.sendAnother}
              </Button>
            </div>

            <p className="mt-8 border-t border-white/[0.06] pt-5 text-xs leading-5 text-zinc-600">
              {copy.successNote}
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080809] text-zinc-100">
      <section className="border-b border-white/10 px-4 py-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <button onClick={() => { window.location.href = '/'; }} className="font-display text-2xl font-black">
            Mec<span className="text-orange-500">Sync</span>
          </button>
          <Button variant="ghost" onClick={() => { window.location.href = '/'; }}>
            <ArrowLeft size={16} />
            {copy.back}
          </Button>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div className="lg:sticky lg:top-8">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-xs font-bold uppercase tracking-wide text-orange-300">
              <Handshake size={16} />
              {copy.badge}
            </div>
            <h1 className="font-display text-4xl font-black leading-tight sm:text-5xl">{copy.headline}</h1>
            <p className="mt-5 text-lg leading-8 text-zinc-400">{copy.intro}</p>

            <div className="mt-8 grid gap-3">
              {copy.highlights.map(([title, text], index) => {
                const Icon = [TrendingUp, Users, ShieldCheck][index];
                return (
                  <div key={title} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-300">
                        <Icon size={20} />
                      </div>
                      <div>
                        <h2 className="font-semibold">{title}</h2>
                        <p className="mt-1 text-sm leading-6 text-zinc-400">{text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={submitApplication} className="rounded-2xl border border-white/10 bg-[#111113] p-5 shadow-2xl shadow-black/30 sm:p-7">
            <h2 className="font-display text-2xl font-bold">{copy.formTitle}</h2>
            <p className="mt-2 text-sm text-zinc-400">{copy.formIntro}</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => updateField('website', event.target.value)} />
              <Input placeholder={copy.fields.name} value={form.nome} onChange={(event) => updateField('nome', event.target.value)} />
              <div className="grid grid-cols-[92px_1fr] gap-3">
                <select className="rounded-lg border border-white/10 bg-[#18181c] px-3 py-2 text-sm text-white focus:border-orange-500 focus:outline-none" value={form.documento_tipo} onChange={(event) => updateField('documento_tipo', event.target.value)}>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="nif">NIF</option>
                  <option value="tax_id">Tax ID</option>
                </select>
                <Input placeholder={copy.fields.document} value={form.documento_numero} onChange={(event) => updateField('documento_numero', form.documento_tipo === 'cpf' || form.documento_tipo === 'cnpj' ? maskCpfCnpj(event.target.value) : event.target.value)} />
              </div>
              <Input type="date" placeholder={copy.fields.birth} value={form.data_nascimento} onChange={(event) => updateField('data_nascimento', event.target.value)} />
              <Input type="email" placeholder={copy.fields.email} value={form.email} onChange={(event) => updateField('email', event.target.value)} />
              <Input mask="phone" placeholder={copy.fields.phone} value={form.telefone} onChange={(event) => updateField('telefone', maskPhone(event.target.value))} />
              <div className="relative">
                <Globe2 className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                <select
                  className="w-full rounded-lg border border-white/10 bg-[#18181c] py-2 pl-9 pr-3 text-sm text-white focus:border-orange-500 focus:outline-none"
                  value={form.pais_codigo}
                  onChange={(event) => {
                    window.localStorage.setItem('mecsync_signup_country', event.target.value);
                    updateField('pais_codigo', event.target.value);
                  }}
                >
                  {Object.entries(COUNTRY_PRESETS).map(([code, preset]) => (
                    <option key={code} value={code}>{countryOptionLabel(code, preset)}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <Input placeholder={copy.fields.city} value={form.cidade} onChange={(event) => updateField('cidade', event.target.value)} />
                <Input placeholder={copy.fields.region} value={form.estado} onChange={(event) => updateField('estado', event.target.value.toUpperCase().slice(0, 32))} />
              </div>
              <Input className="sm:col-span-2" placeholder={copy.fields.experience} value={form.experiencia} onChange={(event) => updateField('experiencia', event.target.value)} />
              <Input className="sm:col-span-2" placeholder={copy.fields.address} value={form.endereco} onChange={(event) => updateField('endereco', event.target.value)} />
              <Input placeholder={copy.fields.district} value={form.bairro} onChange={(event) => updateField('bairro', event.target.value)} />
              <Input mask="cep" placeholder={copy.fields.postal} value={form.cep} onChange={(event) => updateField('cep', maskCep(event.target.value))} />
              <Input className="sm:col-span-2" placeholder={copy.fields.socials} value={form.redes_sociais} onChange={(event) => updateField('redes_sociais', event.target.value)} />
              <select className="rounded-lg border border-white/10 bg-[#18181c] px-3 py-2 text-sm text-white focus:border-orange-500 focus:outline-none" value={form.vende_software} onChange={(event) => updateField('vende_software', event.target.value)}>
                <option value="">{copy.fields.sellsSoftware}</option>
                <option value="sim">{copy.fields.yes}</option>
                <option value="nao">{copy.fields.no}</option>
              </select>
              <Input type="number" min="0" placeholder={copy.fields.portfolio} value={form.carteira_oficinas} onChange={(event) => updateField('carteira_oficinas', event.target.value)} />
            </div>

            <div className="mt-5">
              <p className="mb-2 text-sm font-medium">{copy.fields.channels}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {channelOptions.map((channel) => (
                  <label key={channel.value} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-300">
                    <input type="checkbox" className="accent-orange-500" checked={selectedChannels.has(channel.value)} onChange={() => toggleChannel(channel.value)} />
                    {channel[languageKeyFromCountry(form.pais_codigo)] || channel.pt}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-sm font-medium">{copy.fields.messageTitle}</p>
              <textarea className="min-h-28 w-full rounded-lg border border-white/10 bg-[#18181c] px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-orange-500 focus:outline-none" placeholder={copy.fields.message} value={form.mensagem} onChange={(event) => updateField('mensagem', event.target.value)} />
            </div>

            <label className="mt-5 flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-3 text-sm leading-6 text-zinc-300">
              <input type="checkbox" className="mt-1 accent-orange-500" checked={form.aceita_termos} onChange={(event) => updateField('aceita_termos', event.target.checked)} />
              <span>{copy.fields.terms}</span>
            </label>

            {TURNSTILE_SITE_KEY && (
              <div className="mt-5 flex justify-center">
                <div ref={turnstileContainerRef} />
              </div>
            )}

            <Button type="submit" className="mt-6 w-full justify-center" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Handshake size={16} />}
              {submitting ? copy.fields.sending : copy.fields.submit}
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
}